'use client';

import { useRef } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { CatalogIndexItem } from '@/types/catalog';
import { CatalogCard } from '@/components/catalog/CatalogCard';
import { useT } from '@/hooks/useT';
import { stagger, staggerItem, fadeUp, viewportOnce } from '@/v4/theme/motion';

interface Props {
  title: string;
  href: string;
  items: CatalogIndexItem[];
  eyebrow?: string;
}

export function HomeCarousel({ title, href, items, eyebrow }: Props) {
  const t = useT();
  const scrollRef = useRef<HTMLDivElement>(null);

  function scroll(dir: 'left' | 'right') {
    scrollRef.current?.scrollBy({ left: dir === 'right' ? 680 : -680, behavior: 'smooth' });
  }

  if (!items.length) return null;

  return (
    <motion.section {...fadeUp()}>
      <div className="v4home-sec-hd">
        <div>
          {eyebrow && <p className="v4home-eyebrow">{eyebrow}</p>}
          <h2 className="v4home-sec-title">{title}</h2>
        </div>
        <Link href={href} className="v4home-sec-link">
          {t('common_see_all')} <ChevronRight size={13} strokeWidth={2.5} />
        </Link>
      </div>

      <div className="v4home-car-wrap">
        <button
          className="v4home-car-arrow v4home-car-arrow--l"
          onClick={() => scroll('left')}
          aria-label={t('hero_prev')}
        >
          <ChevronLeft size={18} />
        </button>

        <motion.div
          ref={scrollRef}
          className="v4home-car-track"
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {items.map(item => (
            <motion.div key={item.product_id} variants={staggerItem} className="v4home-car-cell">
              <CatalogCard item={item} />
            </motion.div>
          ))}
        </motion.div>

        <button
          className="v4home-car-arrow v4home-car-arrow--r"
          onClick={() => scroll('right')}
          aria-label={t('hero_next')}
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </motion.section>
  );
}
